import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useRef, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import * as styles from './styles';

export interface ILeasePacketMetadata {
    lpId: number;
    lsNo: number;
    lpNo?: number;
    description?: string;
    fileDate?: string;
    fileType?: string;
    originalFileName: string;
    fileSize: number;
}

interface ILeasePacketModalProps {
    isOpen: boolean;
    leaseNumber: number;
    onClose: () => void;
    onCountChange: (count: number) => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.tif,.tiff,.txt,.eml,.msg,.doc,.docx,.xls,.xlsx,.csv';

export const validateLeasePacketFile = (file: Pick<File, 'name' | 'size'>): string | null => {
    if (file.size > MAX_FILE_SIZE) {
        return 'The selected file exceeds the 50 MB limit.';
    }
    const extension = `.${file.name.split('.').pop() || ''}`.toLowerCase();
    if (!ACCEPTED_FILE_TYPES.split(',').includes(extension)) {
        return 'The selected file type is not supported.';
    }
    return null;
};

const formatFileSize = (bytes: number): string => {
    if (!bytes) {
        return '0 KB';
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (value?: string): string => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const LeasePacketModal: React.FC<ILeasePacketModalProps> = ({ isOpen, leaseNumber, onClose, onCountChange }) => {
    const { get, post, put, del, response } = useFetch();
    const [packets, setPackets] = useState<ILeasePacketMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [description, setDescription] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingDescription, setEditingDescription] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const loadPackets = async (): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            const result = await get(`/v1.0/leases/master/${leaseNumber}/attachments`);
            if (!response.ok || !Array.isArray(result)) {
                throw new Error('Unable to load lease packets.');
            }
            setPackets(result);
            onCountChange(result.length);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to load lease packets.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            void loadPackets();
        } else {
            setError(null);
            setDescription('');
            setSelectedFile(null);
            setEditingId(null);
        }
        // The modal opening and lease identity are the fetch lifecycle boundaries.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, leaseNumber]);

    if (!isOpen) {
        return null;
    }

    const validateFile = (file: File): boolean => {
        const validationError = validateLeasePacketFile(file);
        setError(validationError);
        return validationError === null;
    };

    const upload = async (): Promise<void> => {
        if (!selectedFile || !validateFile(selectedFile)) {
            if (!selectedFile) {
                setError('Select a file to upload.');
            }
            return;
        }
        setBusyId(0);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile, selectedFile.name);
            formData.append('description', description);
            await post(`/v1.0/leases/master/${leaseNumber}/attachments`, formData);
            if (!response.ok) {
                throw new Error('Unable to upload the lease packet.');
            }
            setDescription('');
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            await loadPackets();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to upload the lease packet.');
        } finally {
            setBusyId(null);
        }
    };

    const download = async (packet: ILeasePacketMetadata): Promise<void> => {
        setBusyId(packet.lpId);
        setError(null);
        try {
            await get(`/v1.0/leases/master/${leaseNumber}/attachments/${packet.lpId}/download`);
            if (!response.ok) {
                throw new Error('Unable to download the lease packet.');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = packet.originalFileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to download the lease packet.');
        } finally {
            setBusyId(null);
        }
    };

    const saveDescription = async (packet: ILeasePacketMetadata): Promise<void> => {
        setBusyId(packet.lpId);
        setError(null);
        try {
            await put(`/v1.0/leases/master/${leaseNumber}/attachments/${packet.lpId}`, { description: editingDescription });
            if (!response.ok) {
                throw new Error('Unable to update the description.');
            }
            setEditingId(null);
            await loadPackets();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to update the description.');
        } finally {
            setBusyId(null);
        }
    };

    const replaceFile = async (packet: ILeasePacketMetadata, file?: File): Promise<void> => {
        if (!file || !validateFile(file)) {
            return;
        }
        setBusyId(packet.lpId);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            await put(`/v1.0/leases/master/${leaseNumber}/attachments/${packet.lpId}/file`, formData);
            if (!response.ok) {
                throw new Error('Unable to replace the lease packet.');
            }
            await loadPackets();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to replace the lease packet.');
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (packet: ILeasePacketMetadata): Promise<void> => {
        if (!window.confirm(`Permanently delete "${packet.originalFileName}"?`)) {
            return;
        }
        setBusyId(packet.lpId);
        setError(null);
        try {
            await del(`/v1.0/leases/master/${leaseNumber}/attachments/${packet.lpId}`);
            if (!response.ok) {
                throw new Error('Unable to delete the lease packet.');
            }
            await loadPackets();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to delete the lease packet.');
        } finally {
            setBusyId(null);
        }
    };

    const buttonStyle: React.CSSProperties = {
        ...styles.buttonSecondary,
        minHeight: '2.25rem',
        padding: '0.45rem 0.75rem',
        fontSize: '0.9rem',
        fontWeight: 600,
    };

    const tableCellStyle: React.CSSProperties = {
        padding: '0.8rem 0.7rem',
        verticalAlign: 'middle',
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="lease-packet-title"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                background: 'rgba(17,24,39,.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
            }}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                style={{
                    background: styles.colors.white,
                    borderRadius: '8px',
                    width: 'min(1100px, 96vw)',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 20px 45px rgba(0,0,0,.25)',
                }}
            >
                <div
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        background: styles.colors.white,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1rem 1.25rem',
                        borderBottom: `1px solid ${styles.colors.gray200}`,
                    }}
                >
                    <h2 id="lease-packet-title" style={{ margin: 0, fontSize: '1.2rem' }}>
                        Lease Packet — Lease {leaseNumber}
                    </h2>
                    <button type="button" aria-label="Close lease packet dialog" onClick={onClose} style={buttonStyle}>
                        Close
                    </button>
                </div>

                <div style={{ padding: '1rem 1.25rem' }}>
                    {error && (
                        <div role="alert" style={{ color: styles.colors.danger, marginBottom: '0.75rem' }}>
                            {error}
                        </div>
                    )}

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(180px, 1fr) minmax(240px, 2fr) auto',
                            gap: '0.65rem',
                            alignItems: 'end',
                            padding: '0.85rem',
                            background: styles.colors.gray50,
                            borderRadius: '6px',
                            marginBottom: '1rem',
                        }}
                    >
                        <label style={styles.fieldLabel}>
                            File
                            <LeaseRecordInput
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_FILE_TYPES}
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                style={{ display: 'block', marginTop: '0.3rem', maxWidth: '100%' }}
                            />
                        </label>
                        <label style={styles.fieldLabel}>
                            Description (optional)
                            <LeaseRecordInput
                                value={description}
                                maxLength={120}
                                onChange={(e) => setDescription(e.target.value)}
                                style={{ ...styles.inputBase, display: 'block', marginTop: '0.3rem', width: '100%' }}
                            />
                        </label>
                        <button
                            type="button"
                            onClick={() => void upload()}
                            disabled={busyId !== null}
                            style={{ ...styles.buttonSuccess, padding: '0.45rem 0.8rem' }}
                        >
                            {busyId === 0 ? 'Uploading...' : 'Add'}
                        </button>
                    </div>

                    {loading ? (
                        <p>Loading lease packets...</p>
                    ) : packets.length === 0 ? (
                        <p>No Packet</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem', lineHeight: 1.4 }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: `2px solid ${styles.colors.gray200}` }}>
                                        <th style={{ ...tableCellStyle, fontSize: '0.95rem' }}>Description</th>
                                        <th style={{ ...tableCellStyle, fontSize: '0.95rem' }}>Original Filename</th>
                                        <th style={{ ...tableCellStyle, fontSize: '0.95rem' }}>Size</th>
                                        <th style={{ ...tableCellStyle, fontSize: '0.95rem' }}>Date</th>
                                        <th style={{ ...tableCellStyle, fontSize: '0.95rem' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {packets.map((packet) => (
                                        <tr key={packet.lpId} style={{ borderBottom: `1px solid ${styles.colors.gray200}` }}>
                                            <td style={{ ...tableCellStyle, minWidth: '220px' }}>
                                                {editingId === packet.lpId ? (
                                                    <LeaseRecordInput
                                                        aria-label={`Description for ${packet.originalFileName}`}
                                                        autoFocus={true}
                                                        value={editingDescription}
                                                        maxLength={120}
                                                        onChange={(e) => setEditingDescription(e.target.value)}
                                                        style={{ ...styles.inputBase, width: '100%' }}
                                                    />
                                                ) : (
                                                    packet.description || '—'
                                                )}
                                            </td>
                                            <td style={{ ...tableCellStyle, wordBreak: 'break-word' }}>{packet.originalFileName}</td>
                                            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>{formatFileSize(packet.fileSize)}</td>
                                            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>{formatDate(packet.fileDate)}</td>
                                            <td style={tableCellStyle}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                                    <button
                                                        type="button"
                                                        disabled={busyId !== null}
                                                        onClick={() => void download(packet)}
                                                        style={buttonStyle}
                                                    >
                                                        Download
                                                    </button>
                                                    {editingId === packet.lpId ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                disabled={busyId !== null}
                                                                onClick={() => void saveDescription(packet)}
                                                                style={buttonStyle}
                                                            >
                                                                Save
                                                            </button>
                                                            <button type="button" onClick={() => setEditingId(null)} style={buttonStyle}>
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            disabled={busyId !== null}
                                                            onClick={() => {
                                                                setEditingId(packet.lpId);
                                                                setEditingDescription(packet.description || '');
                                                            }}
                                                            style={buttonStyle}
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                    <label style={{ ...buttonStyle, cursor: busyId === null ? 'pointer' : 'not-allowed' }}>
                                                        Replace
                                                        <LeaseRecordInput
                                                            type="file"
                                                            accept={ACCEPTED_FILE_TYPES}
                                                            disabled={busyId !== null}
                                                            onChange={(e) => {
                                                                void replaceFile(packet, e.target.files?.[0]);
                                                                e.currentTarget.value = '';
                                                            }}
                                                            style={{ display: 'none' }}
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        disabled={busyId !== null}
                                                        onClick={() => void remove(packet)}
                                                        style={{ ...buttonStyle, color: styles.colors.danger }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LeasePacketModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeasePacketModal.tsx